/**
 * 已打款 / 到账记录列表(共享)。数据源 = `GET /wallet/earnings/history` 的 PaidSettlementItem。
 * 收益面板用作「已打款历史」;提现/收款账户页用作「到账记录」(FR-12=B:T+N 自动结算流水,
 * 不另立提现流水)。分页「加载更多」,Web 等价 RN onEndReached。
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEarningsHistory, type PaidSettlementItem } from '@/api/wallet';
import { formatYuan } from '@/lib/formatMoney';

interface PayoutHistoryProps {
  title: string;
  emptyText: string;
}

export function PayoutHistory({ title, emptyText }: PayoutHistoryProps) {
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<PaidSettlementItem[]>([]);
  const historyQuery = useQuery({
    queryKey: ['earnings', 'history', page],
    queryFn: () => getEarningsHistory(page, 20),
  });

  const data = historyQuery.data;
  useEffect(() => {
    if (!data?.items) return;
    // 去重键回退:settlementId 可能缺失 → 退 orderId,避免 undefined 折叠丢行。
    setAccumulated((prev) => {
      const seen = new Set(prev.map(rowKey));
      const fresh = data.items!.filter((x) => !seen.has(rowKey(x)));
      return fresh.length > 0 ? [...prev, ...fresh] : prev;
    });
  }, [data]);

  const items = accumulated;
  // 仅当本页请求成功(data 有值)才信 hasMore;失败时 data 为 undefined,不误判「没有更多了」。
  const loadFailed = historyQuery.isError && !historyQuery.isFetching;
  const hasMore = !!data?.hasMore;

  return (
    <section className="mb-8">
      <h3 className="h3 mb-4">{title}</h3>
      {items.length === 0 && !historyQuery.isLoading && !historyQuery.isError ? (
        <div className="state">
          <p className="muted sm">{emptyText}</p>
        </div>
      ) : items.length === 0 && historyQuery.isError ? (
        <div className="state">
          <p className="muted sm mb-3">记录加载失败,请稍后重试</p>
          <button type="button" className="btn btn-secondary" onClick={() => historyQuery.refetch()}>
            点此重试
          </button>
        </div>
      ) : (
        <div className="list" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>订单</th>
                <th>到账金额</th>
                <th>打款时间</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={rowKey(it)}>
                  <td>
                    <div style={{ fontWeight: 'var(--weight-medium)' }}>
                      {it.productTitle || '商品已下架'}
                    </div>
                    <div className="xs muted mt-1">{it.orderId}</div>
                  </td>
                  <td style={{ color: 'var(--color-primary)', fontWeight: 'var(--weight-semibold)' }}>
                    {formatYuan(it.settledAmount)}
                  </td>
                  <td className="muted">{formatSettleDate(it.settledAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="row" style={{ justifyContent: 'center' }}>
        {historyQuery.isFetching ? (
          <p className="hint mt-4">加载中…</p>
        ) : loadFailed && items.length > 0 ? (
          // 翻页失败:不显「没有更多了」,给重试(既有行仍在)。
          <button type="button" className="btn btn-secondary mt-4" onClick={() => historyQuery.refetch()}>
            加载失败,点此重试
          </button>
        ) : hasMore ? (
          <button
            type="button"
            className="btn btn-secondary mt-4"
            onClick={() => setPage((p) => p + 1)}
          >
            加载更多
          </button>
        ) : items.length > 0 ? (
          <p className="hint mt-4">没有更多了</p>
        ) : null}
      </div>
    </section>
  );
}

/** 行/去重键:settlementId 优先,缺失则退 orderId(避免 undefined 折叠丢行 / key 冲突)。 */
function rowKey(it: PaidSettlementItem): string {
  return it.settlementId || it.orderId || '';
}

function formatSettleDate(iso: string | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
